import React, {useState} from "react";
import Cropper, {Area} from "react-easy-crop";
import {IconSource} from "../../../shared/enums/iconSource";
import {Modal, ModalBody, ModalFooter, ModalHeader} from "reactstrap";
import "./imageEditor.sass";
import {useTranslation} from "react-i18next";
import getCroppedImage from "../../../utils/imageCropUtility";
import NavigationButton from "../../atoms/button/navigationButton";
import ButtonIcon from "../../atoms/button/buttonIcon";
import {getImageEditorOptions, ImageEditorOptions} from "./imageEditorOptions";

export interface IImageEditorProps {
  imageSrc: string;
  onSaveClicked: (editedImage: File) => Promise<void>;
  onCancelClicked: () => void;
  isOpen: boolean;
  title?: string;
  options?: ImageEditorOptions;
}

const ImageEditor: React.FC<IImageEditorProps> = ({
  imageSrc,
  onSaveClicked,
  onCancelClicked,
  isOpen,
  title,
  options
}) => {
  const {t} = useTranslation(["translation"]);

  const [crop, setCrop] = useState({x: 0, y: 0});
  const [cropArea, setCropArea] = useState<Area>();
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [busy, setBusy] = useState(false);

  // Compute the correct options, including shifting max zoom to fit image
  const computeOptions = () => {
    const o = getImageEditorOptions(options);

    // The max size of this image's width
    // Not that the width considers the current rotation, not it's original width
    const maxWidth = (cropArea?.width ?? 0) * zoom;

    // The maximum number of times we can zoom the image, before it is below minWidthInPixels (minimum 1)
    const maxZoom = Math.max(1, Math.floor(maxWidth / o.minWidthInPixels));
    // The actual max zoom can never be larger than options.maxZoom
    const computedMaxZoom = Math.min(o.maxZoom, maxZoom);

    return {
      ...o,
      maxZoom: computedMaxZoom
    };
  };

  const {aspectRatioHeight, aspectRatioWidth, minZoom, maxZoom} =
    computeOptions();

  const closeButton: JSX.Element = (
    <NavigationButton icon={IconSource.exit} onClick={onCancelClicked} />
  );

  // Zoom should never go outside the zoom range
  const handleZoom = (zoom: number) => {
    const newZoom = Math.min(Math.max(zoom, minZoom), maxZoom);
    setZoom(newZoom);
  };

  // mocking the cropping utility for this component is not worthwhile
  /* istanbul ignore next */
  const internalSaveClicked = async () => {
    if (!cropArea) {
      return;
    }

    setBusy(true);
    try {
      const blob = await getCroppedImage(imageSrc, cropArea, rotation, options);
      if (blob) {
        onSaveClicked(blob);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal className="image-editor" isOpen={isOpen} centered={true}>
      <ModalHeader close={closeButton}>
        {title ?? t("translation:controls.image.editImage")}
      </ModalHeader>
      <ModalBody>
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          minZoom={minZoom}
          maxZoom={maxZoom}
          rotation={rotation}
          onZoomChange={handleZoom}
          onCropChange={setCrop}
          onCropComplete={(cropArea, pixelArea) => {
            setCropArea(pixelArea);
          }}
          aspect={aspectRatioWidth / aspectRatioHeight}
          classes={{containerClassName: "cropper"}}
        />
      </ModalBody>
      <ModalFooter>
        <div>
          {zoom === maxZoom
            ? t("translation:controls.image.maxZoomReached")
            : ""}
        </div>
        <div>
          <NavigationButton
            disabled={zoom >= maxZoom}
            icon={IconSource.zoomIn}
            tooltip={t("translation:controls.image.zoomIn")}
            onClick={() => {
              handleZoom(zoom + 1);
            }}
          />
          <NavigationButton
            disabled={zoom <= minZoom}
            tooltip={t("translation:controls.image.zoomOut")}
            icon={IconSource.zoomOut}
            onClick={() => {
              handleZoom(zoom - 1);
            }}
          />
          <NavigationButton
            icon={IconSource.rotate}
            tooltip={t("translation:controls.image.rotate")}
            onClick={() => {
              setRotation(rotation - 90);
            }}
          />
        </div>
        <ButtonIcon
          onClick={internalSaveClicked}
          isDisabled={!cropArea}
          isBusy={busy}
        >
          {t("translation:confirmations.confirm")}
        </ButtonIcon>
      </ModalFooter>
    </Modal>
  );
};

export default ImageEditor;
